import { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import {
  Button,
  Flex,
  Form,
  Modal,
  ModalBody,
  Stack,
  Text,
  Tooltip,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import { useAddFundsForm } from '../hooks/use-add-funds-form';
import { useBillingStripePromise } from '../hooks/use-billing-stripe';
import type { CreditsPurchaseFormView } from '../hooks/use-credits-purchase-form';
import { fundingTitle, type AddFundsFunding, type AutoTopUpSettings } from '../utils/add-funds';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';
import { AddFundsAmountPanel } from './add-funds-amount-panel';
import { AddFundsBenefits } from './add-funds-benefits';
import { AddFundsCardSection } from './add-funds-card-section';
import { AutoTopUpBox } from './auto-top-up-box';
import { FLEX_CREDITS_CONFIG } from '../utils/credits-purchase-config';
import { CreditsPurchaseDisclaimer } from './credits-purchase-disclaimer';

export type AddFundsModalData = {
  currentBalanceCents: number;
  hasFunded: boolean;
  pageName: CreditsPurchasePageName;
  initialAmountDollars?: number;
  funding?: AddFundsFunding;
  onSaveAutoTopUp?: (settings: AutoTopUpSettings) => Promise<void>;
};

export type AddFundsResult = { savedCard: boolean };

export function AddFundsModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<AddFundsModalData, AddFundsResult>) {
  const stripe = useBillingStripePromise();
  const [isProcessing, setIsProcessing] = useState(false);

  return (
    <Modal
      data-test-add-funds-modal
      state={modalState}
      title={data.funding == null ? 'Add funds' : fundingTitle(data.funding)}
      isDismissable={!isProcessing}
    >
      <ModalBody>
        <Elements stripe={stripe}>
          <AddFundsForm data={data} onConfirm={onConfirm} onClose={onClose} onProcessingChange={setIsProcessing} />
        </Elements>
      </ModalBody>
    </Modal>
  );
}

type AddFundsFormProps = {
  data: AddFundsModalData;
  onConfirm: (result: AddFundsResult) => void;
  onClose: () => void;
  onProcessingChange: (isProcessing: boolean) => void;
};

function AddFundsForm({ data, onConfirm, onClose, onProcessingChange }: AddFundsFormProps) {
  const state = useAddFundsForm({ data, onConfirm, onProcessingChange });
  const { form, autoTopUp } = state;

  return (
    <Form autoLoading={false} onSubmit={state.onSubmit}>
      <Stack gap="300">
        <Text color="secondary" data-test-add-funds-subhead>
          {form.hasSavedCard
            ? 'Credits are shared across sending and profiles.'
            : 'Flex: free to start, pay for what you use'}
        </Text>
        {autoTopUp.isActive ? null : (
          <AddFundsAmountPanel
            selection={state.selection}
            balanceCents={data.currentBalanceCents}
            funding={data.funding}
            error={state.amountError}
            isDisabled={state.isSubmitting}
            onChange={state.onSelectionChange}
            onCustomBlur={state.onCustomBlur}
          />
        )}
        <AutoTopUpBox
          settings={autoTopUp.settings}
          errors={autoTopUp.errors}
          balanceCents={data.currentBalanceCents}
          isDisabled={state.isSubmitting}
          onChange={autoTopUp.setSettings}
        />
        {state.cardEntry.isOpen ? null : <AddFundsBenefits isFirstPurchase={!data.hasFunded || !form.hasSavedCard} />}
        <AddFundsCardSection form={form} cardEntry={state.cardEntry} cardError={state.cardError} />
        <CreditsPurchaseDisclaimer disclaimer={FLEX_CREDITS_CONFIG.disclaimer} />
        <TaxLine form={form} isAutoTopUp={autoTopUp.isActive} />
        <AddFundsFooter
          submitLabel={state.submit.label}
          isSubmitDisabled={state.submit.isDisabled}
          isSubmitting={state.isSubmitting}
          tooltip={state.submit.tooltip}
          onClose={onClose}
        />
      </Stack>
    </Form>
  );
}

type AddFundsFooterProps = {
  submitLabel: string;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  tooltip: string;
  onClose: () => void;
};

function AddFundsFooter({ submitLabel, isSubmitDisabled, isSubmitting, tooltip, onClose }: AddFundsFooterProps) {
  return (
    <Flex justifyContent="flex-end" gap="200">
      <Button data-test-add-funds-cancel variant="secondary" isDisabled={isSubmitting} onClick={onClose}>
        Cancel
      </Button>
      <Tooltip content={tooltip} isDisabled={tooltip === ''} isCustom={true} placement="top">
        {(context) => (
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            data-test-add-funds-submit
            variant="primary"
            type="submit"
            isDisabled={isSubmitDisabled}
            isLoading={isSubmitting}
          >
            {submitLabel}
          </Button>
        )}
      </Tooltip>
    </Flex>
  );
}

type TaxLineProps = {
  form: CreditsPurchaseFormView;
  isAutoTopUp: boolean;
};

function TaxLine({ form, isAutoTopUp }: TaxLineProps) {
  const { totalPriceCents, totalWithTaxCents } = form.totals;
  if (isAutoTopUp || totalWithTaxCents <= totalPriceCents) return null;
  return (
    <Text color="secondary" size="sm" data-test-add-funds-tax>
      Total charged {formatCentsToUsd(totalWithTaxCents)} including tax.
    </Text>
  );
}
